import { useQueries, useQuery } from '@tanstack/react-query'
import { client } from '../client'
import { queryKeys } from '../keys'
import type { components } from '@/lib/generated/api-types'

// Type exports for convenience
export type AccountInfoResponse = components['schemas']['AccountInfoResponse']
export type AccountStatus = components['schemas']['AccountStatus']

// Get current user from JWT token
export function useCurrentUser(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.user,
    queryFn: async () => {
      const { data, error } = await client.GET('/api/v1/accounts/me')
      if (error) throw error
      return data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - user info rarely changes
    enabled: options?.enabled ?? true,
  })
}

// Get account status (includes rental info)
export function useAccountStatus(accountName: string | undefined) {
  return useQuery({
    queryKey: queryKeys.accounts.status(accountName!),
    queryFn: async () => {
      const { data, error } = await client.GET(
        '/api/v1/accounts/{account_name}/status',
        {
          params: { path: { account_name: accountName! } },
        },
      )
      if (error) throw error
      return data
    },
    enabled: !!accountName,
    staleTime: 30 * 1000, // 30 seconds - status changes frequently
  })
}

// Get all account statuses for accessible accounts (parallel queries)
// Returns raw API response (same shape as useAccountStatus) to ensure cache consistency
export function useAccountStatuses(
  accountNames: Array<string>,
  enabled = true,
) {
  return useQueries({
    queries: accountNames.map((accountName) => ({
      queryKey: queryKeys.accounts.status(accountName),
      queryFn: async () => {
        const { data, error } = await client.GET(
          '/api/v1/accounts/{account_name}/status',
          {
            params: { path: { account_name: accountName } },
          },
        )
        if (error) throw error
        return data // Raw API response - same shape as useAccountStatus
      },
      enabled: enabled, // Only fetch when explicitly enabled
      staleTime: 30 * 1000,
    })),
    combine: (results) => ({
      // Pair each result with its accountName for easy lookup
      data: accountNames.map((accountName, index) => ({
        accountName,
        status: results[index]?.data ?? null,
        isLoading: results[index]?.isLoading ?? false,
        error: results[index]?.error ?? null,
      })),
      isLoading: results.some((r) => r.isLoading),
      isPending: results.some((r) => r.isPending),
      error: results.find((r) => r.error)?.error ?? null,
      refetch: () => Promise.all(results.map((r) => r.refetch())),
      // Get the oldest dataUpdatedAt from all queries to show data age
      dataUpdatedAt:
        Math.min(
          ...results
            .map((r) => r.dataUpdatedAt)
            .filter((timestamp) => timestamp > 0),
        ) || 0,
    }),
  })
}

// Get next free time for an account
export function useNextFreeTime(
  accountName: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: queryKeys.accounts.nextFreeTime(accountName!),
    queryFn: async () => {
      const { data, error } = await client.GET(
        '/api/v1/accounts/{account_name}/next-free-time',
        {
          params: { path: { account_name: accountName! } },
        },
      )
      if (error) throw error
      return data
    },
    enabled: !!accountName && enabled,
    staleTime: 120 * 1000, // 2 minutes
  })
}

// Get next free times for all accessible accounts (parallel queries)
export function useNextFreeTimes(accountNames: Array<string>, enabled = true) {
  return useQueries({
    queries: accountNames.map((accountName) => ({
      queryKey: queryKeys.accounts.nextFreeTime(accountName),
      queryFn: async () => {
        const { data, error } = await client.GET(
          '/api/v1/accounts/{account_name}/next-free-time',
          {
            params: { path: { account_name: accountName } },
          },
        )
        if (error) throw error
        return data
      },
      enabled,
      staleTime: 120 * 1000,
    })),
    combine: (results) => ({
      data: accountNames.map((accountName, index) => ({
        accountName,
        nextFree: results[index]?.data ?? null,
      })),
      isLoading: results.some((r) => r.isLoading),
    }),
  })
}
